'use client';

import classNames from 'classnames';
import { ComponentProps, Fragment, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import getScreenReaderRatingText from '@alltrails/core/utils/getScreenReaderRatingText';
import { minutesToFormattedTime } from '@alltrails/data-formatting/dateTimeFormatters';
import { getFormattedDistance } from '@alltrails/data-formatting/distanceFormatters';
import { ratingToFormattedRating } from '@alltrails/data-formatting/numberFormatters';
import Link, { type LinkProps } from '@alltrails/denali/components/Link';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import DifficultyIcon from '@alltrails/difficulty/components/DifficultyIcon';
import { getTrailDifficultyLabelFromNumber } from '@alltrails/difficulty/utils/trailDifficultyHelpers';
import Context from '@alltrails/types/Context';
import styles from './styles/styles.module.scss';

type TrailMetadataStatsProps = {
  avgRating?: number;
  compact?: boolean;
  difficulty?: number;
  durationMinutes?: number;
  durationText?: string;
  isDurationMinutesEstimate?: boolean;
  lengthText?: string;
  lengthMeters?: number;
  onDifficultyClick?: ClickHandler;
  onRatingClick?: ClickHandler;
  /** Makes the review count a link, for when it navigates rather than acts on the page */
  ratingLinkProps?: RatingLinkProps;
  reviewCount?: number;
  size: 'sm' | 'md';
  starColor?: ComponentProps<typeof StarFilled>['color'];
};

type ClickHandler = ComponentProps<typeof Link>['onClick'];
type RatingLinkProps = Pick<LinkProps, 'href' | 'target'>;

const linkify = (children: ReactNode, onClick?: ClickHandler, linkProps?: RatingLinkProps) => {
  if (onClick || linkProps) {
    return (
      <Link {...linkProps} onClick={onClick} variant="secondary">
        {children}
      </Link>
    );
  }
  return <span>{children}</span>;
};

const getReviewCountText = (reviewCount: number, compact: boolean) =>
  compact ? (
    String(reviewCount)
  ) : (
    <FormattedMessage defaultMessage="{reviewCount, plural, one {# review} other {# reviews}}" values={{ reviewCount }} />
  );

const TrailMetadataStats = ({
  avgRating,
  compact = false,
  difficulty,
  durationMinutes,
  durationText,
  isDurationMinutesEstimate = false,
  lengthText,
  lengthMeters,
  onDifficultyClick,
  onRatingClick,
  ratingLinkProps,
  reviewCount,
  size,
  starColor = 'currentColor'
}: TrailMetadataStatsProps) => {
  const intl = useIntl();
  const useMetric = useSelector((state: { context: Context }) => state.context?.displayMetric);

  const children: ReactNode[] = [
    avgRating && (
      <>
        <span className={styles.iconContainer} role="img" aria-label={getScreenReaderRatingText(intl, avgRating)}>
          <StarFilled className={classNames(styles.icon, styles.star, styles[size])} color={starColor} size="xs" />
        </span>
        <span>
          {ratingToFormattedRating(avgRating, intl)}
          {reviewCount !== undefined && <> ({linkify(getReviewCountText(reviewCount, compact), onRatingClick, ratingLinkProps)})</>}
        </span>
      </>
    ),
    difficulty && (
      <>
        <span className={styles.iconContainer}>
          <DifficultyIcon className={classNames(styles.icon, styles[size])} difficulty={difficulty} size="xs" />
        </span>
        {linkify(getTrailDifficultyLabelFromNumber(intl, difficulty), onDifficultyClick)}
      </>
    ),
    lengthText ? (
      <span>{lengthText}</span>
    ) : (
      lengthMeters !== undefined && <span>{getFormattedDistance(intl, lengthMeters, useMetric, { maximumFractionDigits: 1 })}</span>
    ),
    durationText ? (
      <span className={styles.truncating}>{durationText}</span>
    ) : (
      !!durationMinutes && (
        <span className={styles.truncating}>
          {minutesToFormattedTime({ intl, totalMinutes: durationMinutes, isEstimate: isDurationMinutesEstimate })}
        </span>
      )
    )
  ].filter(Boolean);

  const hasLink = onDifficultyClick || onRatingClick || ratingLinkProps;

  const content = children.map((child, index) => (
    <Fragment key={`metadata-stat-${index}`}>
      {child}
      {index < children.length - 1 && <span className={classNames(styles.dividerDot, { [styles.compact]: compact })}>·</span>}
    </Fragment>
  ));

  return <div className={classNames(styles.container, styles.truncating, styles[size], { [styles.hasLink]: hasLink })}>{content}</div>;
};

export default TrailMetadataStats;
